import {
    type ReactNode,
    useEffect,
    useCallback,
    useRef,
    useState,
} from "react";
import Icon from "./Icon";

interface BottomSheetProps {
    /** Whether the sheet is visible */
    open: boolean;
    /** Called when user dismisses (backdrop click, swipe, close button) */
    onClose: () => void;
    /** Title in the sheet header */
    title?: string;
    /** Sheet body content */
    children: ReactNode;
    /** Extra classNames for the sheet panel */
    className?: string;
}

export default function BottomSheet({
    open,
    onClose,
    title,
    children,
    className = "",
}: BottomSheetProps): ReactNode {
    const sheetRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);

    // Open animation
    useEffect(() => {
        if (open) {
            setAnimating(true);
            // Small delay for mount → animate
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setVisible(true));
            });
        } else {
            setVisible(false);
            const timer = setTimeout(() => setAnimating(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Close on Escape
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        },
        [onClose]
    );

    useEffect(() => {
        if (open) {
            document.addEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [open, handleKeyDown]);

    if (!open && !animating) return null;

    return (
        <div
            id="bottom-sheet-overlay"
            className={`fixed inset-0 z-[100] flex items-end justify-center transition-colors duration-300 ${visible ? "bg-black/60 backdrop-blur-sm" : "bg-transparent"
                }`}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                ref={sheetRef}
                className={`
          w-full max-w-[430px] bg-card-dark border-t border-white/10 rounded-t-3xl
          transition-transform duration-300 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
          ${className}
        `}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-2">
                    <div className="w-10 h-1 rounded-full bg-white/20" />
                </div>

                {/* Header */}
                {title && (
                    <div className="flex items-center justify-between px-4 xs:px-6 pb-4">
                        <h3 className="text-lg font-extrabold uppercase italic tracking-tight">
                            {title}
                        </h3>
                        <button
                            onClick={onClose}
                            className="flex items-center justify-center size-8 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                        >
                            <Icon name="close" size={18} className="opacity-60" />
                        </button>
                    </div>
                )}

                {/* Body */}
                <div className="px-4 xs:px-6 pb-8 max-h-[70dvh] overflow-y-auto">
                    {children}
                </div>
            </div>
        </div>
    );
}

/* ── Simple centered modal (for alerts, confirmations) ── */
interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: ReactNode;
    className?: string;
}

export function Modal({
    open,
    onClose,
    title,
    children,
    className = "",
}: ModalProps): ReactNode {
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (open) {
            setAnimating(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setVisible(true));
            });
        } else {
            setVisible(false);
            const timer = setTimeout(() => setAnimating(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            const handler = (e: KeyboardEvent) => {
                if (e.key === "Escape") onClose();
            };
            document.addEventListener("keydown", handler);
            document.body.style.overflow = "hidden";
            return () => {
                document.removeEventListener("keydown", handler);
                document.body.style.overflow = "";
            };
        }
    }, [open, onClose]);

    if (!open && !animating) return null;

    return (
        <div
            id="modal-overlay"
            className={`fixed inset-0 z-[100] flex items-center justify-center px-6 transition-colors duration-300 ${visible ? "bg-black/60 backdrop-blur-sm" : "bg-transparent"
                }`}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className={`
          w-full max-w-[380px] bg-card-dark border border-white/10 rounded-lg xs:rounded-2xl p-6
          transition-all duration-300 ease-out
          ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}
          ${className}
        `}
            >
                {title && (
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-extrabold uppercase italic tracking-tight">
                            {title}
                        </h3>
                        <button
                            onClick={onClose}
                            className="flex items-center justify-center size-8 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                        >
                            <Icon name="close" size={18} className="opacity-60" />
                        </button>
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}
